import React, { useEffect } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

export const TILES = {
  // Free OpenStreetMap raster tiles — no API key required
  light: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    subdomains: "abc",
  },
  dark: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    subdomains: "abc",
  },
};

export const MAP_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// Fits the viewport to the given positions whenever they change, instead of a
// fixed world-level zoom that leaves sparse data as a tiny cluster.
export const FitBounds: React.FC<{
  positions: [number, number][];
  positionsKey: string;
  padding?: [number, number];
  maxZoom?: number;
}> = ({ positions, positionsKey, padding = [60, 60], maxZoom = 6 }) => {
  const map = useMap();
  useEffect(() => {
    if (!positions.length) return;
    map.fitBounds(L.latLngBounds(positions), { padding, maxZoom });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, positionsKey]);
  return null;
};

/**
 * Pushpin shape ported from the reference report HTML — a teardrop pin with a
 * metallic stick and a glossy gradient bulb. Bulb colors are CSS custom
 * properties (--pin-c1..c4) so callers can tint per-marker while sharing one
 * stylesheet; defaults match the reference's original red. The label/badge
 * are nested INSIDE .pushpin-marker (not siblings) so their counter-rotation
 * lands them exactly on the bulb regardless of the parent's tilt/scale.
 */
export const PUSHPIN_CSS = `
  .pushpin-icon { background: transparent !important; border: 0 !important; overflow: visible !important; }
  .pushpin-marker {
    display: block; position: relative; width: 34px; height: 58px;
    transform: rotate(14deg) scale(var(--pin-scale, .55)); transform-origin: 50% 100%;
  }
  .pushpin-marker::before {
    content: ""; position: absolute; left: 10px; top: 21px; width: 5px; height: 38px; z-index: 0;
    background: linear-gradient(90deg, #f4f4f4 0%, #8c8c8c 36%, #d9d9d9 62%, #666 100%);
    border-radius: 3px; box-shadow: 1px 1px 2px rgba(0,0,0,.35);
    transform: rotate(15deg); transform-origin: 50% 0;
  }
  .pushpin-marker::after {
    content: ""; position: absolute; left: 3px; top: 0; width: 28px; height: 28px; border-radius: 50%; z-index: 1;
    background: radial-gradient(circle at 34% 35%,
      var(--pin-c1, #f04444) 0%, var(--pin-c2, #d51f25) 42%, var(--pin-c3, #9f1015) 78%, var(--pin-c4, #6f090d) 100%);
    box-shadow: inset -4px -5px 9px rgba(0,0,0,.28), inset 4px 4px 8px rgba(255,255,255,.22), 0 2px 5px rgba(0,0,0,.28);
  }
  .pin-wrap { position: relative; width: 72px; height: 58px; overflow: visible; }
  .pin-wrap .pushpin-marker { position: absolute; left: 19px; top: 0; }
  .pin-label {
    position: absolute; left: 3px; top: 0; z-index: 2; width: 28px; height: 28px;
    display: flex; align-items: center; justify-content: center;
    color: #fff; font: 700 8px/1 Arial, sans-serif; text-align: center;
    text-shadow: 0 1px 2px rgba(0,0,0,.55); white-space: nowrap; pointer-events: none;
    transform: rotate(-14deg); transform-origin: 50% 50%;
  }
  .pin-glyph {
    position: absolute; left: 3px; top: 0; z-index: 2; width: 28px; height: 28px;
    display: flex; align-items: center; justify-content: center; pointer-events: none;
    transform: rotate(-14deg); transform-origin: 50% 50%;
  }
  .pin-type-badge {
    position: absolute; left: 22px; top: -3px; z-index: 3; width: 14px; height: 14px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    border: 1.5px solid #fff; box-shadow: 0 1px 2px rgba(0,0,0,.4); pointer-events: none;
    transform: rotate(-14deg); transform-origin: 50% 50%;
  }
`;

// Shared dark-glass popup chrome (Leaflet's default popup is white/light-only)
export const popupCss = (tooltipBg: string, shadowLg: string) => `
  .leaflet-popup.geo-popup .leaflet-popup-content-wrapper {
    background: ${tooltipBg}; border-radius: 10px; box-shadow: ${shadowLg};
    border: 1px solid rgba(255,255,255,0.08);
  }
  .leaflet-popup.geo-popup .leaflet-popup-content { margin: 12px 16px; }
  .leaflet-popup.geo-popup .leaflet-popup-tip { background: ${tooltipBg}; box-shadow: none; }
  .leaflet-popup.geo-popup .leaflet-popup-close-button { color: rgba(255,255,255,0.6) !important; }
`;

// Hover-triggered tooltip chrome (Leaflet Tooltip opens on mouseover automatically,
// unlike Popup which requires a click) — same dark-glass look as popupCss.
export const tooltipCss = (tooltipBg: string, shadowLg: string) => `
  .leaflet-tooltip.geo-hover-tip {
    background: ${tooltipBg}; color: rgba(255,255,255,0.92);
    border: 1px solid rgba(255,255,255,0.08); border-radius: 10px;
    box-shadow: ${shadowLg}; padding: 8px 12px; font-size: 12px; opacity: 1 !important;
  }
  .leaflet-tooltip.geo-hover-tip.leaflet-tooltip-top::before { border-top-color: ${tooltipBg}; }
  .leaflet-tooltip.geo-hover-tip.leaflet-tooltip-bottom::before { border-bottom-color: ${tooltipBg}; }
`;
